import { StatsigProvider, useClientAsyncInit } from '@statsig/react-bindings';
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics';
import { ReactNode, useEffect, useRef } from 'react';

import { AnalyticsEvent } from '@/hooks/useAnalytics';
import { useAuth } from '@/state/auth';

// StatsigWrapper provides feature flag context using user authentication
function StatsigWrapper({ children }: { children: ReactNode }) {
  const { me, friend, principal, ssoDomain } = useAuth();
  const previousMe = useRef(me); // previousMe is used to detect login transitions.

  // Extract organization domain from email
  const organizationId = me ? me.split('@')[1] || '' : '';

  const statsigUser = {
    userID: principal || 'anonymous',
    email: me || '',
    custom: {
      account: friend || me, // If friend isn't set, the account is the personal tenant.
      organizationID: organizationId,
      isPraetorian: me ? me.endsWith('@praetorian.com') : false,
      isImpersonating: !!friend,
    },
  };

  const clientKey = import.meta.env.VITE_STATSIG_CLIENT_KEY || '';

  // Use async initialization hook to fetch latest feature flag values
  const { client } = useClientAsyncInit(clientKey, statsigUser, {
    plugins: [new StatsigAutoCapturePlugin()],
  });

  // Update Statsig user identity when auth state changes (e.g., after login)
  // useClientAsyncInit only sets the user on first initialization;
  // this effect syncs the user when me/friend/sub/ssoDomain/userType change.
  useEffect(() => {
    // Detect logout: me transitioned from populated to empty
    if (client && previousMe.current && !me) {
      client.logEvent(AnalyticsEvent.AUTH_LOGOUT);
      previousMe.current = me;
      return;
    }

    if (!client || !me) return;

    client.updateUserAsync(statsigUser);

    // Detect login: me transitioned from empty to populated
    if (!previousMe.current) {
      client.logEvent(AnalyticsEvent.AUTH_LOGIN, undefined, {
        method: ssoDomain ? 'sso' : 'password',
        sso_domain: ssoDomain || '',
      });
    }

    previousMe.current = me;
  }, [client, me, friend, principal, ssoDomain]);

  // Always wrap in StatsigProvider so child components can safely use hooks like useGateValue
  // The provider will handle the loading state internally
  // When user is not signed in, gates will return false (default) which is the expected behavior
  return <StatsigProvider client={client}>{children}</StatsigProvider>;
}

export default StatsigWrapper;
